import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector, touch } from 'redux-form'
// view components
import Option from './components/Option'
import OptionForm from 'components/Modals/PropertyDetailsModal/components/Form/components/CustomOptions/components/OptionForm'
// views
import { LabelWrapper } from './views'
import { AddButton, Wrapper } from 'components/Modals/PropertyDetailsModal/components/Form/view'
// types
import { Forms } from 'constants/index'
import { IOption } from 'components/FormElements/Select'
import { IFieldOption } from 'types/graphql'
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
// utils
import { v4 as uuid } from 'uuid'

interface Props {
  isDisabled: boolean
}

const selector = formValueSelector(Forms.PROPERTY_EDIT_FORM)

const CustomOptions: React.FC<WrappedFieldArrayProps<any> & Props> = ({ fields, isDisabled }) => {
  const dispatch = useDispatch()

  const options = useSelector((state) => selector(state, 'options')) || []

  const changeField = (value: IFieldOption[]) => dispatch(change(Forms.PROPERTY_EDIT_FORM, 'options', value))

  const removeOption = (id: string) => () => {
    const filteredOptions = options.filter((property) => property.id !== id)

    changeField(filteredOptions)
  }

  const reorder = (list: IFieldOption[], startIndex: number, endIndex: number) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)
    reorderedList.splice(endIndex, 0, removed)
    return reorderedList
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const reorderedOptions = reorder(options, source.index, destination.index)

      changeField(reorderedOptions)
    }
  }

  const removeField = (idx: number) => fields.remove(idx)

  const createOption = (field: string, data: IOption, idx: number) => () => {
    const { label, value } = data

    const fields = [`${field}.label`, `${field}.value`]

    dispatch(touch(Forms.PROPERTY_EDIT_FORM, ...fields))

    if (!label || !value) {
      return
    }

    const id = uuid()

    changeField([...options, { id, label, value, active: true }])
    removeField(idx)
  }

  const addField = () => fields.push({})

  return (
    <>
      {!!options.length && (
        <LabelWrapper>
          <div>Label</div>
          <div>Internal Value</div>
        </LabelWrapper>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={'OPTIONS'}>
          {(provided) => (
            <div ref={provided.innerRef}>
              {options.map((option, index) => (
                <Draggable key={option.id} draggableId={option.id} index={index} isDragDisabled={isDisabled}>
                  {(provided) => (
                    <Wrapper
                      ref={provided.innerRef}
                      key={option.value}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={provided.draggableProps.style as React.CSSProperties}>
                      <Option option={option} removeOption={removeOption(option.id)} isDisabled={isDisabled} />
                    </Wrapper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <OptionForm fields={fields} removeField={removeField} createOption={createOption} />
      {!isDisabled && (
        <AddButton data-testid='picklist-add-option' $disabled={fields.length > 0} onClick={addField}>
          + add option
        </AddButton>
      )}
    </>
  )
}

export default CustomOptions
